/**
 * Public-website HTTP client (task 4.5, public-website spec).
 *
 * Per design §3.1 (API client layer), this module is a thin `fetch`
 * wrapper that injects the public API key (read from `/config.json` at
 * SPA bootstrap) and the Cognito ID token (when present in the auth
 * slice) into per-request headers. The wrapper itself does NOT read
 * `/config.json` or the Redux auth slice directly — those values are
 * plumbed in by the consuming API client wrappers (content-api-client,
 * games-api-client, profile-api-client, etc., which land in tasks 5.x,
 * 6.x, and 8.x). Keeping the http-client side-effect-free keeps it
 * trivially testable and lets the same wrapper serve every per-contract
 * client in the SPA.
 *
 * Header rules:
 * - When `apiKey` is set on the request init, an `x-api-key: <apiKey>`
 *   header is added. This is the public REST API key required by every
 *   non-Profile* route per the API Gateway usage plan.
 * - When `bearerToken` is set, an `Authorization: Bearer <bearerToken>`
 *   header is added. The Profile* routes require this; every other
 *   route ignores it but accepts it without error.
 * - Existing `headers` on the request init are preserved verbatim;
 *   the injected headers are merged on top so an explicit
 *   per-request override always wins. (This matters for the SWR cache
 *   key in task 4.6 — an explicit `Authorization` override flows
 *   through to `cache key includes both` per design §3.1.)
 *
 * Function-length budget: every public export (and every helper
 * declared below) stays under 20 lines per Requirement 3.7. The header
 * merge is decomposed into `mergeHeaders` so `httpClient` itself is a
 * three-line composition.
 */

/**
 * Per-request init for `httpClient` extending the platform `RequestInit`
 * with two optional auth-bearing fields.
 */
export interface HttpClientRequestInit extends RequestInit {
  /**
   * Public API key sourced from `/config.json` at SPA bootstrap. When
   * set, the wrapper adds `x-api-key: <apiKey>` to the outgoing request.
   */
  apiKey?: string;
  /**
   * Cognito ID token sourced from the auth slice when a session is
   * present. When set, the wrapper adds `Authorization: Bearer
   * <bearerToken>` to the outgoing request.
   */
  bearerToken?: string;
}

/**
 * Build the auth-injected header bag for a request. Existing entries
 * on `init.headers` win over the injected `apiKey` / `bearerToken`
 * (callers can therefore override either header explicitly).
 *
 * @param init The per-request init (possibly with `apiKey`,
 *   `bearerToken`, and/or `headers`).
 * @returns A `Headers` instance ready to hand to `fetch`.
 */
export function mergeHeaders(init?: HttpClientRequestInit): Headers {
  const headers = new Headers();
  if (init?.apiKey) headers.set('x-api-key', init.apiKey);
  if (init?.bearerToken) headers.set('Authorization', `Bearer ${init.bearerToken}`);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }
  return headers;
}

/**
 * Strip the auth fields from a `HttpClientRequestInit` so the result
 * is a plain `RequestInit` that `fetch` accepts without complaint.
 *
 * @param init The per-request init.
 * @returns A `RequestInit` with `apiKey` / `bearerToken` removed.
 */
function stripAuthFields(init?: HttpClientRequestInit): RequestInit {
  if (!init) return {};
  const { apiKey: _apiKey, bearerToken: _bearerToken, headers: _headers, ...rest } = init;
  return rest;
}

/**
 * Fetch wrapper that injects the public API key and Cognito ID token
 * into the outgoing request headers per design §3.1.
 *
 * @param input The request URL or `Request` object.
 * @param init Optional per-request init. Set `apiKey` to add an
 *   `x-api-key` header; set `bearerToken` to add an `Authorization:
 *   Bearer <token>` header.
 * @returns The `Response` returned by `fetch`.
 */
export function httpClient(
  input: RequestInfo | URL,
  init?: HttpClientRequestInit,
): Promise<Response> {
  const headers = mergeHeaders(init);
  const rest = stripAuthFields(init);
  return fetch(input, { ...rest, headers });
}
